import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ClockIcon, LinkIcon, LockIcon, UserIcon } from 'lucide-react';
import {
  AuthShell,
  BrandMark,
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconInput,
  PasswordInput,
  ResultState,
} from '@opengoal/ui';
import { ApiError } from '@opengoal/http';
import { useTranslation } from 'react-i18next';
import { env } from '@/app/env';
import { agenciesControllerActivateV1 } from '@/api/generated/agencies/agencies';
import { setSessionToken } from '@/lib/session';
import { SignedInBanner } from '../components/SignedInBanner';

/**
 * Where `{INVITE_LINK_BASE_URL}/activate-agency?token=…` lands.
 *
 * The first step of the whole chain: an admin created the agency, the backend
 * emailed its root user this link, and redeeming it creates their user account
 * and makes them the agency's owner.
 *
 * Unlike every other auth screen this does NOT go through `authClient`.
 * `POST /v1/agencies/activate` is a plain Nest endpoint that creates the account
 * and returns a ready-to-use bearer token in one call, so there is no separate
 * sign-in step afterwards — just store the token.
 */
export function ActivateAgencyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_public/activate-agency' });
  const [error, setError] = useState<ApiError | null>(null);

  const schema = z.object({
    name: z.string().trim().min(1, t('auth.nameRequired')),
    password: z.string().min(1, t('auth.choosePassword')).min(8, t('auth.passwordTooShort')),
  });
  type ActivateFormValues = z.infer<typeof schema>;

  const form = useForm<ActivateFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', password: '' },
  });

  const activate = useMutation({
    mutationFn: (values: ActivateFormValues) =>
      agenciesControllerActivateV1({
        token: search.token ?? '',
        name: values.name,
        password: values.password,
      }),
    onSuccess: async (result) => {
      setSessionToken(result.token);
      // Anything cached under a previous session (including a different user's
      // profile, if they were signed in when they opened the link) is stale now.
      await queryClient.invalidateQueries();
      void navigate({ to: '/' });
    },
    onError: (mutationError: unknown) => {
      setError(mutationError instanceof ApiError ? mutationError : null);
    },
  });

  const brand = <BrandMark name={env.appName} tagline={t('app.tagline')} />;

  // A link that has been used already, or hand-truncated, arrives with no token.
  if (!search.token) {
    return (
      <AuthShell brand={brand} title={t('auth.activateTokenMissingTitle')}>
        <ResultState
          tone="warning"
          icon={<LinkIcon />}
          description={t('auth.activateTokenMissingBody')}
          className="px-0 py-2"
          action={
            <Button asChild>
              <Link to="/login">{t('auth.signIn')}</Link>
            </Button>
          }
        />
      </AuthShell>
    );
  }

  // 400 means the seven-day window elapsed. There is nothing the user can do
  // from here — only an admin can reissue — so say that rather than offering a
  // retry that will fail identically.
  if (error?.status === 400) {
    return (
      <AuthShell brand={brand} title={t('auth.activateExpiredTitle')}>
        <ResultState
          tone="warning"
          icon={<ClockIcon />}
          description={t('auth.activateExpiredBody')}
          className="px-0 py-2"
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      brand={brand}
      title={t('auth.activateTitle')}
      subtitle={t('auth.activateSubtitleGeneric')}
      aside={{ heading: t('auth.activateAsideHeading'), body: t('auth.activateAsideBody') }}
      footer={
        <Link to="/login" className="text-muted-foreground hover:text-foreground">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      <SignedInBanner />

      <Form {...form}>
        <form
          noValidate
          className="grid gap-5"
          onSubmit={form.handleSubmit((values) => {
            setError(null);
            activate.mutate(values);
          })}
        >
          {/* No email field: the address is fixed by the invitation and the
              backend reads it from the agency, so offering to change it here
              would be a lie. */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.fullName')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<UserIcon />}
                    autoComplete="name"
                    placeholder={t('auth.fullNamePlaceholder')}
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    icon={<LockIcon />}
                    autoComplete="new-password"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error ? (
            <Callout
              variant="destructive"
              title={
                error.status === 404 ? t('auth.activateTokenMissingBody') : t('auth.errors.generic')
              }
            />
          ) : null}

          <Button type="submit" size="lg" className="w-full" loading={activate.isPending}>
            {t('auth.activateCta')}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
